// Make sure we're importing React properly
import React from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

// Create the context
const ThemeContext = React.createContext(null);

// Export the context for direct use
export default ThemeContext;

const FONT_SCALE = { small: 0.875, medium: 1, large: 1.125 };
const DENSITY_SPACING = { compact: 6, standard: 8, comfortable: 10 };

// Creates theme objects with the given primary color, font scale and spacing
const buildTheme = (mode, primaryColor = '#49A0AD', fontScale = 1, spacingBase = 8) => {
  const isDark = mode === 'dark';
  return createTheme({
    spacing: spacingBase,
    palette: {
      mode,
      primary: { main: primaryColor },
      background: isDark
        ? { default: '#0F1214', paper: '#151A1E' }
        : { default: '#F5F5F5', paper: '#FFFFFF' },
      text: isDark
        ? { primary: '#E6E6E6', secondary: '#A0A6AD' }
        : { primary: '#000000', secondary: '#666666' },
      divider: isDark ? '#2A2F35' : '#E5E5E5',
      error: { main: isDark ? '#FF6B6B' : '#FF3B30' },
      success: { main: '#4CAF50' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14 * fontScale,
      button: { textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    components: {
      ...(isDark ? { MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } } } : {}),
      MuiButton: { styleOverrides: { root: { borderRadius: 20, paddingLeft: 16, paddingRight: 16 } } },
      MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: 20 } } } },
    },
  });
};

// Detect system preference
const getSystemMode = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch { return 'light'; }
};

// Resolve the effective mode from preference ('light'|'dark'|'system')
const resolveMode = (preference) => {
  if (preference === 'system') return getSystemMode();
  return preference === 'dark' ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [themePreference, setThemePreferenceState] = React.useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('theme_preference') : null;
      return saved === 'dark' || saved === 'light' || saved === 'system' ? saved : 'light';
    } catch { return 'light'; }
  });

  const [fontSize, setFontSizeState] = React.useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('theme_font_size') : null;
      return saved === 'small' || saved === 'medium' || saved === 'large' ? saved : 'medium';
    } catch { return 'medium'; }
  });

  const [density, setDensityState] = React.useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('theme_density') : null;
      return saved === 'compact' || saved === 'standard' || saved === 'comfortable' ? saved : 'standard';
    } catch { return 'standard'; }
  });

  const [primaryColor, setPrimaryColor] = React.useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('theme_primary_color') : null;
      return saved || '#49A0AD';
    } catch { return '#49A0AD'; }
  });

  // Track system preference changes when mode is 'system'
  const [systemMode, setSystemMode] = React.useState(() => getSystemMode());

  React.useEffect(() => {
    if (themePreference !== 'system') return;
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch {}
  }, [themePreference]);

  const effectiveMode = themePreference === 'system' ? systemMode : themePreference;

  const theme = React.useMemo(() => {
    const fontScale = FONT_SCALE[fontSize] || 1;
    const spacingBase = DENSITY_SPACING[density] || 8;
    return buildTheme(effectiveMode, primaryColor, fontScale, spacingBase);
  }, [effectiveMode, primaryColor, fontSize, density]);

  const toggleTheme = React.useCallback(() => {
    setThemePreferenceState((prev) => {
      const next = resolveMode(prev) === 'dark' ? 'light' : 'dark';
      return next;
    });
  }, []);

  const setThemeMode = React.useCallback((mode) => {
    if (mode === 'light' || mode === 'dark' || mode === 'system') {
      setThemePreferenceState(mode);
    }
  }, []);

  const setFontSize = React.useCallback((size) => {
    if (size === 'small' || size === 'medium' || size === 'large') setFontSizeState(size);
  }, []);

  const setDensity = React.useCallback((d) => {
    if (d === 'compact' || d === 'standard' || d === 'comfortable') setDensityState(d);
  }, []);

  const changePrimaryColor = React.useCallback((color) => {
    if (typeof color === 'string' && color) setPrimaryColor(color);
  }, []);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('theme_preference', themePreference);
        // Keep legacy key in sync for sidebar toggle
        window.localStorage.setItem('theme_mode', effectiveMode);
      }
    } catch {}
  }, [themePreference, effectiveMode]);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('theme_font_size', fontSize);
    } catch {}
  }, [fontSize]);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('theme_density', density);
    } catch {}
  }, [density]);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('theme_primary_color', primaryColor);
    } catch {}
  }, [primaryColor]);

  const themeContextValue = React.useMemo(() => ({
    theme,
    isDarkMode: effectiveMode === 'dark',
    toggleTheme,
    themePreference,
    setThemeMode,
    fontSize,
    setFontSize,
    density,
    setDensity,
    primaryColor,
    changePrimaryColor,
  }), [theme, effectiveMode, toggleTheme, themePreference, setThemeMode, fontSize, setFontSize, density, setDensity, primaryColor, changePrimaryColor]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};